import { useLayoutEffect, useRef, useState } from 'react';

import { fitFontSize } from './headerFit.js';

export type HeaderMeasure = (text: string, px: number, element: HTMLElement) => number;

const measureText: HeaderMeasure = (text, px, element) => {
  const probe = document.createElement('span');
  probe.textContent = text;
  probe.setAttribute('aria-hidden', 'true');
  Object.assign(probe.style, {
    position: 'absolute',
    visibility: 'hidden',
    whiteSpace: 'pre',
    width: 'max-content',
    fontSize: `${String(px)}px`,
    letterSpacing: '-0.02em',
  });
  element.append(probe);
  const width = probe.getBoundingClientRect().width;
  probe.remove();
  return width;
};

/**
 * Both sides of `.shell__team`'s inline padding (3px each, fixed px in
 * shell.css). A constant rather than a read of the computed style, so the
 * available width cannot depend on the size the fit last applied.
 */
const INLINE_PADDING = 6;

/** A classic scrollbar's width at the current zoom, 0 where scrollbars overlay. */
function scrollbarWidth() {
  const probe = document.createElement('div');
  Object.assign(probe.style, {
    position: 'absolute',
    visibility: 'hidden',
    overflow: 'scroll',
    width: '100px',
    height: '100px',
  });
  document.body.append(probe);
  const width = probe.offsetWidth - probe.clientWidth;
  probe.remove();
  return width;
}

export function useHeaderFit(name: string, measure: HeaderMeasure = measureText) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [fit, setFit] = useState<{ name: string; px: number } | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    const container = element?.parentElement;
    if (
      element === null ||
      container === null ||
      container === undefined ||
      typeof ResizeObserver === 'undefined'
    ) {
      setFit(null);
      return;
    }
    let active = true;
    let current: number | undefined;
    const update = () => {
      if (!active) return;
      const max = parseFloat(getComputedStyle(element).getPropertyValue('--shell-team-max')) || 21;
      const available = container.getBoundingClientRect().width - INLINE_PADDING;
      if (available <= 0) return;
      const px = fitFontSize({
        measure: (size) => measure(name, size, element),
        available,
        max,
        current,
        slack: scrollbarWidth(),
      });
      current = px;
      setFit((previous) =>
        previous?.name === name && previous.px === px ? previous : { name, px },
      );
    };
    // A font swap changes every measurement, so it fits afresh instead of growing by the rule.
    const refit = () => {
      current = undefined;
      update();
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    void document.fonts?.ready.then(refit);
    document.fonts?.addEventListener('loadingdone', refit);
    return () => {
      active = false;
      observer.disconnect();
      document.fonts?.removeEventListener('loadingdone', refit);
    };
  }, [name, measure]);
  return {
    ref,
    px: fit?.name === name ? fit.px : undefined,
  };
}
